import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { FC, memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GiHamburgerMenu } from "react-icons/gi";
import { IoBookOutline } from "react-icons/io5";
import { MdHeadset } from "react-icons/md";
import { useParams } from "react-router";
import { useIsMobile } from "~/hook/use-mobile";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import useNavigateAudioBook from "~/hook/useNavigateAudioBook";
import { gradientVariants } from "~/lib/motion-obj";
import { cn } from "~/lib/utils";
import { FALLBACK_IMAGE_ALT_TEXT, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import DownloadButton from "./DownloadButton";
import ImageWithSkeleton from "./ImageWithSkeleton";

// TYPES
interface ImageProps {
  alt: string;
  aspectRatio?: string;
  pdf_path: string;
  fileName: string;
  img_aspect?: string;
  isBookPart?: number;
  isAudio?: boolean;
  blur_hash?: string;
  url?: UrlType;
  playlist_id?: number | null;
  handleAudioPlay?: (id: number | null) => void;
}

type UrlType = {
  small: string;
  medium: string;
  large: string;
  hd: string;
  original: string;
};

type BooksType = {
  blur_hash?: string;
  name: string;
  pdf_path: string;
  img_aspect?: string;
  isBookPart?: number;
  navigatePath?: string;
  hasAudio?: boolean;
  tag?: string;
  url?: UrlType;
  downLoadFineName: string;
  playlist_id?: number | null;
};

// MAIN COMPONENT
const DisplayBookCard: FC<BooksType> = memo(
  ({ name, pdf_path, img_aspect, isBookPart, navigatePath, hasAudio, blur_hash, url, downLoadFineName, tag, playlist_id }) => {
    const { LocalizedLink } = useLocale();
    const { medium, normalInter } = useFontStyle();
    const lang = useParams().lang as "en" | "gu";
    const { handleNavigateAudioBook } = useNavigateAudioBook(lang || LANG.english);

    const handleAudioPlay = async (playlistId: number | null) => {
      if (!playlistId) return;
      await handleNavigateAudioBook(playlistId);
    };
    const stableUrl = useMemo<UrlType>(
      () => ({
        small: url?.small || FALLBACK_IMAGE_URL,
        medium: url?.medium || FALLBACK_IMAGE_URL,
        large: url?.large || FALLBACK_IMAGE_URL,
        hd: url?.hd || FALLBACK_IMAGE_URL,
        original: url?.original || FALLBACK_IMAGE_URL,
      }),
      [url],
    );

    const Tag = useMemo(
      () =>
        tag && (
          <div className="absolute top-2 right-2 overflow-hidden bg-cardBg px-2 rounded-lg border border-textHeadingColor flex items-center justify-center">
            <div
              className={cn(
                "flex items-center justify-center text-textColor tracking-wider text-xs sm:text-sm md:text-sm font-semibold text-center w-full truncate capitalize",
                normalInter,
              )}
            >
              <span title={tag}>{tag}</span>
            </div>
          </div>
        ),
      [tag],
    );

    const CardContent = (
      <div>
        <Image
          fileName={downLoadFineName}
          pdf_path={pdf_path}
          aspectRatio={img_aspect}
          alt={name || "Book Image"}
          isBookPart={isBookPart}
          isAudio={hasAudio}
          blur_hash={blur_hash}
          img_aspect={img_aspect}
          url={stableUrl}
          handleAudioPlay={handleAudioPlay}
          playlist_id={playlist_id}
        />
        {Tag}
      </div>
    );

    const CardWrapper = (
      <>
        {CardContent}
        <div className="py-2 2xl:py-3">
          {/* tracking-wider if MokuBrush */}
          <div className={cn("text-textColor body_text_m text-center w-full truncate capitalize", medium)}>
            <span title={name}>{name}</span>
          </div>
        </div>
      </>
    );

    return (
      <div className="bg-cardBgColor rounded-[7px] relative flex flex-col justify-between gap-2 h-full hover:shadow-sm hover:shadow-textHeadingColor transition-all duration-300 ease-in-out">
        {navigatePath ? (
          navigatePath.toLowerCase().endsWith(".pdf") ? (
            <a href={navigatePath} target="_blank" rel="noopener noreferrer" className="rounded-[7px] h-full w-full">
              {CardWrapper}
            </a>
          ) : (
            <LocalizedLink to={navigatePath} prefetch="intent" className="rounded-[7px] h-full w-full">
              {CardWrapper}
            </LocalizedLink>
          )
        ) : (
          <div className="rounded-[7px] h-full w-full">{CardWrapper}</div>
        )}
      </div>
    );
  },
);

const Image: FC<ImageProps> = ({ alt, fileName, pdf_path, isBookPart, isAudio, blur_hash, url, handleAudioPlay, playlist_id }) => {
  return (
    <div className="relative w-full overflow-hidden rounded-t-[7px] bg-gray-100">
      <div className="w-full aspect-[2/3] rounded-t-lg overflow-hidden">
        <ImageWithSkeleton
          url={url!}
          alt={alt || FALLBACK_IMAGE_ALT_TEXT}
          blur_hash={blur_hash}
          isPreloaded={true}
          className="w-full h-full object-cover rounded-t-lg"
        />
      </div>

      {isBookPart && isBookPart > 1 ? (
        <MultipartSection part={isBookPart} />
      ) : (
        <>
          <ShowOptions pdfUrl={pdf_path} fileName={fileName} isAudio={isAudio} handlePlayAudio={handleAudioPlay} playlist_id={playlist_id} />
          <div className="absolute bottom-0 right-0 left-0 w-full bg-satsangSevakGradient max-lg:flex hidden cursor-pointer">
            <DownloadButtons pdfUrl={pdf_path} fileName={fileName} isAudio={isAudio} handlePlayAudio={handleAudioPlay} playlist_id={playlist_id} />
          </div>
        </>
      )}
    </div>
  );
};

Image.displayName = "Image";

Image.propTypes = {
  alt: PropTypes.string.isRequired,
  aspectRatio: PropTypes.string,
  pdf_path: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  img_aspect: PropTypes.string,
  isBookPart: PropTypes.number,
  isAudio: PropTypes.bool,
  handleAudioPlay: PropTypes.func,
  blur_hash: PropTypes.string,
  url: PropTypes.shape({
    small: PropTypes.string.isRequired,
    medium: PropTypes.string.isRequired,
    large: PropTypes.string.isRequired,
    hd: PropTypes.string.isRequired,
    original: PropTypes.string.isRequired,
  }),
  playlist_id: PropTypes.number,
};

// DOWNLOAD SECTION
const ShowOptions = memo(
  ({
    pdfUrl,
    fileName,
    isAudio = false,
    handlePlayAudio,
    playlist_id = null,
  }: {
    pdfUrl: string;
    fileName: string;
    isAudio?: boolean;
    playlist_id?: number | null;
    handlePlayAudio?: (id: number | null) => void;
  }) => {
    const isMobile = useIsMobile();
    const [isVisible, setIsVisible] = useState(isMobile ? true : false);
    return (
      <div
        className="w-full opacity-100 cursor-pointer h-full"
        onMouseEnter={() => {
          if (isMobile) return;
          setIsVisible(true);
        }}
        onMouseLeave={() => {
          if (isMobile) return;
          setIsVisible(false);
        }}
      >
        <motion.div
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          exit="exit"
          variants={gradientVariants}
          className="flex items-end justify-center absolute inset-0 w-full h-full bg-satsangSevakGradient rounded-[7px]"
        >
          <DownloadButtons pdfUrl={pdfUrl} fileName={fileName} isAudio={isAudio} handlePlayAudio={handlePlayAudio} playlist_id={playlist_id} />
        </motion.div>
      </div>
    );
  },
);

ShowOptions.displayName = "ShowOptions";

const DownloadButtons = memo(
  ({
    pdfUrl,
    fileName,
    isAudio = false,
    handlePlayAudio,
    playlist_id = null,
  }: {
    pdfUrl: string;
    fileName: string;
    isAudio?: boolean;
    playlist_id?: number | null;
    handlePlayAudio?: (id: number | null) => void;
  }) => {
    const handleRead = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    };

    return (
      <div className="w-full flex justify-center items-center gap-2 mb-4">
        <DownloadButton fileName={fileName} url={pdfUrl} />
        <button title="Read Book" className="book-card-item" onClick={handleRead}>
          <IoBookOutline size={21} color="#D97E57" />
        </button>
        {isAudio ? (
          <button
            title="Listen"
            className="book-card-item"
            onClick={(e) => {
              e.preventDefault();
              handlePlayAudio && handlePlayAudio(playlist_id);
            }}
          >
            <MdHeadset size={22} color="#D97E57" />
          </button>
        ) : null}
      </div>
    );
  },
);
DownloadButtons.displayName = "DownloadButtons";

const MultipartSection = memo(({ part }: { part: number }) => {
  const { medium } = useFontStyle();
  const { t } = useTranslation();
  return (
    <div className={cn("absolute bottom-0 right-0 bg-textHeadingColor rounded-tl-lg p-1.5 text-xs text-white flex gap-x-1", medium)}>
      <GiHamburgerMenu className="text-white text-[1rem] " />
      <span className="body_text_xs ">{t("common.parts", { count: part })}</span>
    </div>
  );
});

MultipartSection.displayName = "MultipartSection";

DisplayBookCard.displayName = "DisplayBookCard";

DisplayBookCard.propTypes = {
  name: PropTypes.string.isRequired,
  pdf_path: PropTypes.string.isRequired,
  img_aspect: PropTypes.string,
  isBookPart: PropTypes.number,
  navigatePath: PropTypes.string,
  hasAudio: PropTypes.bool,
  blur_hash: PropTypes.string,
  url: PropTypes.shape({
    small: PropTypes.string.isRequired,
    medium: PropTypes.string.isRequired,
    large: PropTypes.string.isRequired,
    hd: PropTypes.string.isRequired,
    original: PropTypes.string.isRequired,
  }),
  downLoadFineName: PropTypes.string.isRequired,
  tag: PropTypes.string,
  playlist_id: PropTypes.number,
};

export default DisplayBookCard;
